import { useEffect, useState, useRef } from "react";
import { useUserContext } from "../contexts/UserContext";
import { useLocation } from "react-router-dom";
import { useNavigate } from 'react-router';
import { authClient } from '../utils/amplifyClient';
import * as Sentry from "@sentry/browser";

import { SignInHeader } from "../components/Auth/SignInHeader/SignInHeader";
import { SignInFooter } from "../components/Auth/SignInFooter/SignInFooter";
import { SignUpHeaderFreeDemo } from "../components/Auth/SignUpHeader/SignUpHeader";

import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { iLoginProps } from "../types";
import { authenticatorServices } from "../utils/authenticatorServices";

import { fetchUserAttributes } from "aws-amplify/auth";
import { User } from "../types/schema";
import { peekPostLoginPath } from "../utils/postLoginPath";
import {
  ensureFreeDemoCompanyOwner,
  clearFreeDemoSignup,
  isFreeDemoSignupPending,
  markFreeDemoSignup,
} from "../utils/createFreeDemoOwner";
import { companyOwnersByCognitoId, getCompany, usersByCognitoId, businessAdminsByCognitoId, listBusinessAdmins, listUsers, listCompanyOwners } from "../graphql/queries";
import { updateBusinessAdmin as updateBusinessAdminMutation, updateUser as updateUserMutation, updateCompanyOwner as updateCompanyOwnerMutation } from "../graphql/mutations";
import TotalEquipIQLogoStacked from '../components/ui/icons-logos/TotalEquipIQLogoStacked';
import { BackButton } from '../components/ui';

export const Login = ({ logIn, initialState }: iLoginProps) => {

  const { toForgotPassword } = useAuthenticator();
  const { user, route } = useAuthenticator((context) => {
    return [context.user, context.route];
  });
  const { getUserDetails, setBusinessAdmin } = useUserContext();
  const navigate = useNavigate();
  const location = useLocation();


  const [, setIsValidatingUser] = useState(false);
  const [userIsValid, setUserIsValid] = useState(false);
  const validationAttempted = useRef(false);

  // Free-demo self-serve signup lives on these routes. Do not rely on sessionStorage
  // from the marketing splash — users often open /signup directly.
  const isFreeDemoPath =
    location.pathname === '/signup' || location.pathname === '/register/free-demo';

  useEffect(() => {
    if (isFreeDemoPath || !logIn) {
      markFreeDemoSignup();
    }
  }, [isFreeDemoPath, logIn]);

  useEffect(() => {
    // Invite flows (/register/:id, /register/admin/:id, …) link records themselves.
    // /register/free-demo is self-serve and must run the same validation as /signup.
    const isInviteRegistrationPage =
      location.pathname.startsWith('/register/') &&
      location.pathname !== '/register/free-demo';
    if (user && route === "authenticated" && !validationAttempted.current && !isInviteRegistrationPage) {
      validationAttempted.current = true;
      setIsValidatingUser(true);
      validateUserInDatabase(user.userId);
    }
  }, [user, route, location.pathname]);

  const validateUserInDatabase = async (userId: string) => {
    try {

      // Check if BusinessAdmin exists first
      const baExists = await checkIfBusinessAdminExists(userId);
      if (baExists) {
        setUserIsValid(true);
        return;
      }

      // Check if owner exists
      const ownerExists = await checkIfOwnerExists(userId);
      if (ownerExists) {
        setUserIsValid(true);
        return;
      }

      // Check if user exists
      await checkIfUserExists(userId); // ✅ Now it will handle the logic internally

    } catch (error) {
      console.error('User validation failed:', error);
      Sentry.captureException(error);
      setUserIsValid(true); // Prevent infinite retry - let UserContext handle auth
    } finally {
      setIsValidatingUser(false);
    }
  };

  const checkIfBusinessAdminExists = async (cognitoId: string): Promise<boolean> => {
    try {
      const response = await authClient.graphql({
        query: businessAdminsByCognitoId,
        variables: { cognitoId },
      });

      const items = response.data.businessAdminsByCognitoId.items;
      if (items && items.length > 0) {
        setBusinessAdmin(items[0]);
        return true;
      }
      return false;
    } catch (error) {
      console.log('BusinessAdmin lookup failed, falling through to CompanyOwner check', error);
      Sentry.captureException(error);
      return false;
    }
  };

  /**
   * Attempts to link a pre-enrolled BusinessAdmin record to this Cognito user.
   * When a BusinessAdmin is enrolled via the admin homepage, the record is created
   * with an empty cognitoId. This function looks up BusinessAdmin records by the
   * authenticated user's email and, if a match with an empty cognitoId is found,
   * updates it with the real cognitoId.
   * 
   * TODO: Replace with Lambda-based approach (post-confirmation trigger) for robustness.
   */
  const linkBusinessAdminByCognitoId = async (cognitoId: string): Promise<boolean> => {
    try {
      const attributes = await fetchUserAttributes();
      const email = attributes.email?.toLowerCase();
      if (!email) return false;

      // Query all BusinessAdmins and filter by email with empty/missing cognitoId
      const response = await authClient.graphql({
        query: listBusinessAdmins,
        variables: {
          filter: { email: { eq: email } },
        },
      });

      // Match by email AND (empty/pending cognitoId OR a stale cognitoId that
      // differs from the current sub — the cross-account migration case, same as
      // linkUserByEmail). Only reached after a lookup by the current sub found nothing.
      const matches = response.data.listBusinessAdmins?.items?.filter((item) => {
        if (!item || item.email?.toLowerCase() !== email) return false;
        const existing = item.cognitoId;
        if (!existing || existing === "" || existing.startsWith("pending-")) return true;
        return existing !== cognitoId; // stale sub → migrated user
      });

      if (!matches || matches.length === 0) return false;

      // Update the first matching record with the real cognitoId
      const baRecord = matches[0]!;
      const updateResponse = await authClient.graphql({
        query: updateBusinessAdminMutation,
        variables: {
          input: {
            id: baRecord.id,
            cognitoId: cognitoId,
          },
        },
      });

      setBusinessAdmin(updateResponse.data.updateBusinessAdmin);
      console.log(`BusinessAdmin record ${baRecord.id} linked to cognitoId ${cognitoId}`);
      return true;
    } catch (error) {
      console.log('BusinessAdmin email-based linking failed:', error);
      Sentry.captureException(error);
      return false;
    }
  };

  /**
   * Attempts to link a pre-enrolled User record to this Cognito user.
   * When a user is invited via UsersContent, the User record is created with
   * an empty cognitoId; the cognitoId is expected to be filled in when the
   * user completes the `/register/<userId>` flow. If that linking step never
   * ran (e.g. the user signed up through Cognito directly, or the register
   * page encountered an error), the user ends up CONFIRMED in Cognito but
   * unable to log in to the app — `usersByCognitoId` returns empty and they
   * bounce to `/account` with "Account Not Found".
   *
   * This fallback matches by the authenticated user's email and, if a matching
   * User record with an empty/missing cognitoId is found, updates it with the
   * real cognitoId so subsequent sign-ins go through the normal path.
   *
   * TODO: Replace with a Cognito post-confirmation Lambda trigger for robustness.
   */
  const linkUserByEmail = async (cognitoId: string): Promise<User | null> => {
    try {
      const attributes = await fetchUserAttributes();
      const email = attributes.email?.toLowerCase();
      if (!email) return null;

      const response = await authClient.graphql({
        query: listUsers,
        variables: {
          filter: { email: { eq: email } },
        },
      });

      // Relink a User row to this Cognito identity when its email matches AND either:
      //  - it has no real cognitoId yet (pending invite / empty / "pending-"), or
      //  - it has a STALE cognitoId that differs from the current sub. This is the
      //    CROSS-ACCOUNT MIGRATION case: the lazy UserMigration trigger creates the
      //    user in the new pool with a brand-new sub, but the DynamoDB row still holds
      //    the old pool's sub. Without this, migrated users hit "Account not found" on
      //    their first login because usersByCognitoId(newSub) returns nothing here and
      //    this fallback previously only matched empty/pending rows.
      // Safe because checkIfUserExists only calls this after a lookup by the current
      // sub already returned no rows.
      const matches = response.data.listUsers?.items?.filter((item) => {
        if (!item || item.email?.toLowerCase() !== email) return false;
        const existing = item.cognitoId;
        if (!existing || existing === "" || existing.startsWith("pending-")) return true;
        return existing !== cognitoId; // stale sub → migrated user
      });

      if (!matches || matches.length === 0) return null;

      const userRecord = matches[0]!;
      const updateResponse = await authClient.graphql({
        query: updateUserMutation,
        variables: {
          input: {
            id: userRecord.id,
            cognitoId: cognitoId,
          },
        },
      });

      console.log(`User record ${userRecord.id} linked to cognitoId ${cognitoId}`);
      return updateResponse.data.updateUser as User;
    } catch (error) {
      console.log('User email-based linking failed:', error);
      Sentry.captureException(error);
      return null;
    }
  };

  // Reset validation state when user logs out
  useEffect(() => {
    if (!user) {
      setUserIsValid(false);
      setIsValidatingUser(false);
    }
  }, [user]);

  // Return to the in-app page opened before sign-in, otherwise Home.
  useEffect(() => {
    if (userIsValid && user && route === "authenticated") {
      navigate(peekPostLoginPath() ?? '/', { replace: true });
    }
  }, [userIsValid, user, route, navigate]);

  const checkIfOwnerExists = async (cognitoId: string): Promise<boolean> => {

    const response = await authClient.graphql({
      query: companyOwnersByCognitoId,
      variables: { cognitoId },
    });

    if (response.data.companyOwnersByCognitoId.items.length > 0) {
      setUserIsValid(true);
      return true;
    }

    // Migrated CompanyOwner: the by-cognitoId lookup missed because the record
    // still holds the OLD pool's sub. Relink by email (same cross-account
    // migration case as linkUserByEmail / linkBusinessAdminByCognitoId). Without
    // this, a migrated CompanyOwner (e.g. a super-admin) hits "Account not found"
    // on first login.
    const relinked = await linkCompanyOwnerByEmail(cognitoId);
    setUserIsValid(relinked);
    return relinked;
  };

  /**
   * Attempts to link a CompanyOwner record to this Cognito user by email when the
   * by-cognitoId lookup found nothing. Handles pending-invite (empty/pending
   * cognitoId) AND cross-account migration (populated-but-stale cognitoId that
   * differs from the current sub). Only called after the by-sub lookup returned
   * nothing.
   */
  const linkCompanyOwnerByEmail = async (cognitoId: string): Promise<boolean> => {
    try {
      const attributes = await fetchUserAttributes();
      const email = attributes.email?.toLowerCase();
      if (!email) return false;

      const response = await authClient.graphql({
        query: listCompanyOwners,
        variables: { filter: { email: { eq: email } }, limit: 1000 },
      });

      const matches = response.data.listCompanyOwners?.items?.filter((item) => {
        if (!item || item.email?.toLowerCase() !== email) return false;
        const existing = item.cognitoId;
        if (!existing || existing === "" || existing.startsWith("pending-")) return true;
        return existing !== cognitoId; // stale sub → migrated owner
      });

      if (!matches || matches.length === 0) return false;

      const ownerRecord = matches[0]!;
      await authClient.graphql({
        query: updateCompanyOwnerMutation,
        variables: { input: { id: ownerRecord.id, cognitoId } },
      });
      console.log(`CompanyOwner record ${ownerRecord.id} linked to cognitoId ${cognitoId}`);
      return true;
    } catch (error) {
      console.log('CompanyOwner email-based linking failed:', error);
      Sentry.captureException(error);
      return false;
    }
  };

  const checkIfUserExists = async (cognitoId: string) => {
    const response = await authClient.graphql({
      query: usersByCognitoId,
      variables: { cognitoId },
    });

    if (response.data.usersByCognitoId.items.length > 0) {
      const companyExists = await checkIfUserCompanyExists(response.data.usersByCognitoId.items[0].companyId as string)

      if (companyExists) {
        // Load user details into context before navigating
        await getUserDetails();
      }
      setUserIsValid(companyExists)
    } else {

      // Check if this is a pre-enrolled BusinessAdmin signing in for the first time.
      // When a BusinessAdmin is enrolled via the admin homepage, the record is created
      // with an empty cognitoId. On first Cognito sign-up/sign-in, we match by email
      // and update the record with the real cognitoId.
      const linkedAsBA = await linkBusinessAdminByCognitoId(cognitoId);
      if (linkedAsBA) {
        setUserIsValid(true);
        return;
      }

      // Check if this is a pre-enrolled User (invited via UsersContent) signing in
      // for the first time without having completed `/register/<userId>` linking.
      // Match the Cognito account to the pending User record by email.
      const linkedUser = await linkUserByEmail(cognitoId);
      if (linkedUser) {
        const companyExists = await checkIfUserCompanyExists(linkedUser.companyId as string);
        if (companyExists) {
          await getUserDetails();
        }
        setUserIsValid(companyExists);
        return;
      }

      const isFreeDemoSignup =
        isFreeDemoSignupPending() ||
        isFreeDemoPath ||
        !logIn;

      if (isFreeDemoSignup) {
        await createFreeDemoOwner();
        clearFreeDemoSignup();
      } else {
        // No app record and not free-demo signup — send to account status
        navigate('/account', { replace: true });
      }

    }
  };

  const checkIfUserCompanyExists = async (companyId: string): Promise<boolean> => {
    const response = await authClient.graphql({
      query: getCompany,
      variables: { id: companyId }
    })

    return !!response.data.getCompany
  }

  const createFreeDemoOwner = async () => {
    try {
      await ensureFreeDemoCompanyOwner(user?.userId);
      await getUserDetails();
      setUserIsValid(true);
    } catch (error) {
      console.error("Error creating free demo owner:", error);
      Sentry.captureException(error);
      navigate('/account', { replace: true });
    }
  };




  const components = {
    SignIn: {
      Header() {
        return <SignInHeader />;
      },
      Footer() {
        return (
          <SignInFooter
            toForgotPassword={toForgotPassword}
            forgotPasswordText="Forgot Password"
          />
        );
      },
    },
    SignUp: {
      Header() {
        return <SignUpHeaderFreeDemo />
      },
    },
  };
  const formFields = !logIn
    ? {
      signUp: {
        name: {
          placeholder: "Enter Your Name Here",
          isRequired: true,
          label: "Name:",
        },
      },
    }
    : {
      signIn: {
        username: {
          placeholder: 'Enter Your Email Here',
          isRequired: true,
          label: 'Email:'
        },
      },
    };
  return (
    <div className="login-page">
      <div className="login-header">
        <div className="login-logo-wrap">
          <TotalEquipIQLogoStacked iconClasses="login-logo" ariaLabel="TotalEquipIQ" />
        </div>
        <div className="login-button-div">
          <BackButton to="/" buttonText="Back to Home" />
          {!logIn && (
            <button className="login-btn" onClick={() => window.location.href = '/login'}>
              Sign In
            </button>
          )}
        </div>
      </div>

      <Authenticator
        key={initialState}
        services={authenticatorServices}
        initialState={initialState}
        components={components}
        formFields={formFields}
        loginMechanisms={["email"]}
        hideSignUp={logIn}
      />
    </div>
  );
};
